import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { UnreadMessagesProvider } from '@/contexts/UnreadMessagesContext';
import { Toaster } from 'sonner';
import '@/i18n';
// Import axios interceptor configuration to handle errors globally
import '@/utils/axiosConfig';

// Pages
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import Files from '@/pages/Files';
import FileDetail from '@/pages/FileDetail';
import Employees from '@/pages/Employees';
import Clients from '@/pages/Clients';
import ClientsList from '@/pages/ClientsList';
import ClientGroups from '@/pages/ClientGroups';
import Messages from '@/pages/Messages';
import MessagingPermissions from '@/pages/MessagingPermissions';
import Quotes from '@/pages/Quotes';
import Invoices from '@/pages/Invoices';
import Profile from '@/pages/Profile';
import AuditLogs from '@/pages/AuditLogs';
import DocumentApprovals from '@/pages/DocumentApprovals';
import WorkflowTracker from '@/pages/WorkflowTracker';
import Timesheet from '@/pages/Timesheet';
import RH from '@/pages/RH';
import MonDossier from '@/pages/MonDossier';
import Payments from '@/pages/Payments';
import RoleManagement from '@/pages/RoleManagement';
import GestionPaie from '@/pages/rh/GestionPaie';
import Tresorerie from '@/pages/Tresorerie';
import CompteClient from '@/pages/CompteClient';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/dashboard" /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
      <Route path="/files/:fileId" element={<ProtectedRoute><FileDetail /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/clients-list" element={<ProtectedRoute><ClientsList /></ProtectedRoute>} />
      <Route path="/client-groups" element={<ProtectedRoute><ClientGroups /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messaging-permissions" element={<ProtectedRoute><MessagingPermissions /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/document-approvals" element={<ProtectedRoute><DocumentApprovals /></ProtectedRoute>} />
      <Route path="/workflow-tracker" element={<ProtectedRoute><WorkflowTracker /></ProtectedRoute>} />
      <Route path="/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
      <Route path="/rh" element={<ProtectedRoute><RH /></ProtectedRoute>} />
      <Route path="/mon-dossier" element={<ProtectedRoute><MonDossier /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/gestion-paie" element={<ProtectedRoute><GestionPaie /></ProtectedRoute>} />
      <Route path="/tresorerie" element={<ProtectedRoute><Tresorerie /></ProtectedRoute>} />
      <Route path="/compte-client" element={<ProtectedRoute><CompteClient /></ProtectedRoute>} />
      <Route path="/role-management" element={<ProtectedRoute><RoleManagement /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <UnreadMessagesProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </UnreadMessagesProvider>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;